import type { PropTypes } from './MobileMenu.types';
import type { ReactElement } from 'react';

import { Box, Flex, Popover, Text } from '@rango-dev/whale';
import { NavItem } from '@rango-dev/whale/lab';
import clsx from 'clsx';
import { Ellipsis } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { useAppSelector } from '../../hooks/reduxHooks';
import { getContainer, isActiveLink } from '../../utils/generalFunctions/common';
import navLinkStyles from '../NavLinks/NavLinks.module.css';
import { NavMoreMenu } from '../NavMoreMenu';

import styles from './MobileMenu.module.css';

const MORE_ICON_SIZE = 20;
const MORE_POPOVER_SIDE_OFFSET = 8;
const MORE_POPOVER_COLLISION_PADDING = 8;

export function MobileMenu(props: PropTypes): ReactElement {
  const { links, moreGroups } = props;
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { language } = useAppSelector((state) => state.settings);
  const [openMore, setOpenMore] = useState(false);

  return (
    <Flex asChild align="stretch">
      <nav className={styles.container}>
        {links.map((link) => {
          const isActive = isActiveLink(pathname, link.location, false, language);

          return (
            <NavItem
              id={link.id}
              asChild
              key={link.id}
              active={isActive}
              className={clsx(styles.item, isActive && styles.itemActive, link.className)}>
              <Link
                id={link.id}
                to={link.location}
                onClick={link.onClick}
                className={styles.linkItem}>
                {link.icon}
                <Text size="1" weight="medium">
                  {link.title}
                </Text>
              </Link>
            </NavItem>
          );
        })}

        <Popover.Root open={openMore} onOpenChange={setOpenMore}>
          <Popover.Trigger>
            <button
              type="button"
              id="mobile-menu-more-item-btn"
              className={clsx(styles.item, openMore && styles.itemActive)}>
              <Box position="relative">
                <Ellipsis size={MORE_ICON_SIZE} />
                <Box className={navLinkStyles.moreBadge}>
                  <Text size="1" className="beamerTrigger" />
                </Box>
              </Box>
              <Text size="1" weight="medium">
                {t('More')}
              </Text>
            </button>
          </Popover.Trigger>
          <Popover.Content
            forceMount
            side="top"
            align="end"
            sideOffset={MORE_POPOVER_SIDE_OFFSET}
            collisionPadding={MORE_POPOVER_COLLISION_PADDING}
            container={getContainer()}
            className={styles.morePopover}>
            <NavMoreMenu groups={moreGroups} onNavigate={() => setOpenMore(false)} />
          </Popover.Content>
        </Popover.Root>
      </nav>
    </Flex>
  );
}
